/** Indicações cirúrgicas / de procedimento — oftalmologia (grade OD/OE/AO por item). */

export interface OphthalmologyIndicationItem {
  key: string;
  label: string;
}

export interface OphthalmologyIndicationGroup {
  title: string;
  items: OphthalmologyIndicationItem[];
}

export const OPHTHALMOLOGY_INDICATION_GROUPS: OphthalmologyIndicationGroup[] = [
  {
    title: 'LENTES DE CONTATO',
    items: [
      { key: 'ind_lc_gelatinosa', label: 'Gelatinosa' },
      { key: 'ind_lc_rigida_corneana', label: 'Rígida Corneana' },
      { key: 'ind_lc_rigida_escleral', label: 'Rígida Escleral' },
    ],
  },
  {
    title: 'LASER',
    items: [
      { key: 'ind_laser_argonio_panfoto', label: 'Argônio — Panfoto' },
      { key: 'ind_laser_argonio_foto_focal', label: 'Argônio — Foto focal' },
      { key: 'ind_laser_yag_capsulotomia', label: 'YAG laser — Capsulotomia' },
      { key: 'ind_laser_yag_iridotomia', label: 'YAG laser — Iridotomia' },
    ],
  },
  {
    title: 'CATARATA',
    items: [
      { key: 'ind_cat_convencional', label: 'Catarata convencional' },
      { key: 'ind_cat_complexa', label: 'Catarata complexa' },
      { key: 'ind_cat_congenita', label: 'Catarata congênita' },
    ],
  },
  {
    title: 'CONJUNTIVA E PELE',
    items: [
      { key: 'ind_cp_pterigio', label: 'Pterígio' },
      { key: 'ind_cp_calazio', label: 'Calázio' },
      { key: 'ind_cp_exerese_lesoes', label: 'Exérese de lesões' },
      { key: 'ind_cp_exerese_bolsao', label: 'Exérese de bolsão lipídico' },
      { key: 'ind_cp_epilacao_cilios', label: 'Epilação de cílios' },
    ],
  },
  {
    title: 'GLAUCOMA',
    items: [
      { key: 'ind_gl_trabeculectomia', label: 'Trabeculectomia' },
      { key: 'ind_gl_agulhamento', label: 'Agulhamento' },
      { key: 'ind_gl_valvula', label: 'Válvula filtrante' },
      { key: 'ind_gl_ciclofoto', label: 'Ciclofotocoagulação' },
    ],
  },
  {
    title: 'RETINA',
    items: [
      { key: 'ind_ret_vvpp', label: 'VVPP' },
      { key: 'ind_ret_faixa_buckle', label: 'Faixa e Buckle' },
      { key: 'ind_ret_retirada_oleo', label: 'Retirada de óleo' },
    ],
  },
  {
    title: 'INJEÇÕES INTRAVÍTREAS',
    items: [
      { key: 'ind_ivt_avt', label: 'AVT' },
      { key: 'ind_ivt_eylia', label: 'Eylia' },
    ],
  },
  {
    title: 'PLÁSTICA',
    items: [
      { key: 'ind_plast_blef_sup', label: 'Blefaroplastia superior' },
      { key: 'ind_plast_blef_inf', label: 'Blefaroplastia inferior' },
      { key: 'ind_plast_ptose', label: 'Ptose' },
      { key: 'ind_plast_entropio', label: 'Entrópio' },
      { key: 'ind_plast_ectropio', label: 'Ectrópio' },
      { key: 'ind_plast_evisceracao', label: 'Evisceração / Enucleação' },
    ],
  },
  {
    title: 'CÓRNEA',
    items: [
      { key: 'ind_cor_refrativa', label: 'Refrativa' },
      { key: 'ind_cor_crosslinking', label: 'Crosslinking' },
      { key: 'ind_cor_transplante', label: 'Transplante' },
      { key: 'ind_cor_anel_intraestromal', label: 'Anel intraestromal' },
    ],
  },
];

export type OphthalmologyIndicationSelection = Record<
  string,
  { od: boolean; oe: boolean; ao: boolean }
>;

const allIndicationKeys: string[] = OPHTHALMOLOGY_INDICATION_GROUPS.flatMap((g) =>
  g.items.map((i) => i.key),
);

export function emptyOphthalmologyIndicationSelections(): OphthalmologyIndicationSelection {
  return Object.fromEntries(allIndicationKeys.map((k) => [k, { od: false, oe: false, ao: false }]));
}

/** Rótulos para `procedures_performed` / chips da evolução. */
export function indicationWorkflowLabelsFromSelections(
  selections: OphthalmologyIndicationSelection,
): string[] {
  const out: string[] = [];
  for (const g of OPHTHALMOLOGY_INDICATION_GROUPS) {
    for (const row of g.items) {
      const s = selections[row.key];
      if (!s || (!s.od && !s.oe && !s.ao)) continue;
      const parts: string[] = [];
      if (s.od) parts.push('OD');
      if (s.oe) parts.push('OE');
      if (s.ao) parts.push('AO');
      out.push(`${row.label} (${parts.join(', ')})`);
    }
  }
  return out;
}

export function buildOphthalmologyIndicationsBody(
  selections: OphthalmologyIndicationSelection,
  outrosText: string,
  observacoes: string,
): string {
  const lines: string[] = ['INDICAÇÕES — OFTALMOLOGIA', ''];

  for (const g of OPHTHALMOLOGY_INDICATION_GROUPS) {
    const groupLines: string[] = [];
    for (const row of g.items) {
      const s = selections[row.key];
      if (!s || (!s.od && !s.oe && !s.ao)) continue;
      const od = s.od ? '✓' : ' ';
      const oe = s.oe ? '✓' : ' ';
      const ao = s.ao ? '✓' : ' ';
      const label = row.label.length > 42 ? `${row.label.slice(0, 39)}…` : row.label;
      groupLines.push(label.padEnd(44) + od.padStart(4) + '  ' + oe.padStart(4) + '  ' + ao.padStart(4));
    }
    if (groupLines.length === 0) continue;
    lines.push(g.title);
    lines.push('Procedimento'.padEnd(44) + 'OD'.padStart(4) + '  ' + 'OE'.padStart(4) + '  ' + 'AO'.padStart(4));
    lines.push('─'.repeat(58));
    lines.push(...groupLines);
    lines.push('');
  }

  if (outrosText.trim()) {
    lines.push('Outros — especificar:', outrosText.trim(), '');
  }
  if (observacoes.trim()) {
    lines.push('Observações:', observacoes.trim(), '');
  }

  lines.push('Legenda: OD = olho direito · OE = olho esquerdo · AO = ambos os olhos');

  return lines.join('\n').trim();
}
